import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase-config';
import { useAuth } from '../context/AuthContext';

const functions = getFunctions();
const createTranscriptCheckoutFn = httpsCallable(functions, 'createTranscriptCheckout');

const LESSON_LABELS = ['Assessment', 'Foundation', 'Application', 'Synthesis'];

export default function SessionReport() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const action = searchParams.get('action') || 'pause'; // 'pause' | 'done'

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfReady, setPdfReady] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  useEffect(() => {
    if (!currentUser || !sessionId) {
      navigate('/dashboard');
      return;
    }
    async function loadSession() {
      try {
        const snap = await getDoc(doc(db, 'chat_sessions', sessionId));
        if (snap.exists()) {
          const data = snap.data();
          // Verify ownership
          if (data.uid !== currentUser.uid) {
            navigate('/dashboard');
            return;
          }
          setSession(data);
          if (!data.transcriptPurchased) {
            // Transcript not yet purchased — show purchase CTA, skip PDF polling
            return;
          }
          if (data.pdfUrl) {
            setDownloadUrl(data.pdfUrl);
            setPdfReady(true);
          } else {
            // PDF not yet ready — poll for up to 30s
            let attempts = 0;
            const interval = setInterval(async () => {
              attempts++;
              const refreshed = await getDoc(doc(db, 'chat_sessions', sessionId));
              if (refreshed.exists() && refreshed.data().pdfUrl) {
                setDownloadUrl(refreshed.data().pdfUrl);
                setPdfReady(true);
                setSession(refreshed.data());
                clearInterval(interval);
              }
              if (attempts >= 15) clearInterval(interval); // give up after ~30s
            }, 2000);
          }
        }
      } catch (err) {
        console.error('Failed to load session:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSession();
  }, [currentUser, sessionId, navigate]);

  async function handlePurchaseTranscript() {
    setTranscriptLoading(true);
    try {
      const result = await createTranscriptCheckoutFn({ sessionId, action });
      window.location.href = result.data.url;
    } catch {
      setTranscriptLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.center}>
          <div style={styles.spinner} />
          <p style={styles.subtext}>Loading your session…</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={styles.page}>
        <div style={styles.center}>
          <p style={styles.subtext}>Session not found.</p>
          <button onClick={() => navigate('/dashboard')} style={styles.btnSecondary}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const lessonCount = session.lessonCount || 0;
  const isDone = action === 'done' || lessonCount >= 4;
  const dateStr = session.lastMessage?.toDate?.()?.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  }) || 'Today';

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logo}>THE OPERATOR METHOD</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => navigate('/dashboard')} style={styles.backBtn}>
            ← Dashboard
          </button>
          <button onClick={() => { sessionStorage.setItem('om_intentional_signout', '1'); signOut(auth).then(() => navigate('/login')); }} style={styles.signOutBtn}>
            Sign out
          </button>
        </div>
      </div>

      <div style={styles.content}>

        {/* Title block */}
        <div style={styles.titleBlock}>
          <div style={styles.tagline}>
            {isDone ? 'Block Complete' : 'Session Paused'}
          </div>
          <h1 style={styles.title}>
            {isDone
              ? 'You finished your first training block.'
              : "You've saved your progress."}
          </h1>
          <p style={styles.subtitle}>{dateStr}</p>
        </div>

        {/* Session summary card */}
        <div style={styles.summaryCard}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Lessons completed</span>
            <span style={styles.summaryValue}>{lessonCount} / 4</span>
          </div>
          {session.assessedLevel && (
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Assessed level</span>
              <span style={{ ...styles.summaryValue, ...styles.levelChip }}>
                {session.assessedLevel}
              </span>
            </div>
          )}
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Session ID</span>
            <span style={{ ...styles.summaryValue, color: '#4a5568', fontSize: '12px' }}>
              {sessionId}
            </span>
          </div>

          {/* Progress pips */}
          <div style={styles.pipsRow}>
            {LESSON_LABELS.map((label, i) => (
              <div key={label} style={styles.pipItem}>
                <div style={{
                  ...styles.pip,
                  backgroundColor: i < lessonCount ? '#4f8ef7' : '#0d1220',
                  borderColor: i < lessonCount ? '#4f8ef7' : '#1e2a3a',
                }}>
                  <span style={{ fontSize: '11px', color: i < lessonCount ? '#fff' : '#3a4560' }}>
                    {i < lessonCount ? '✓' : i + 1}
                  </span>
                </div>
                <span style={{ ...styles.pipLabel, color: i < lessonCount ? '#8a9bbf' : '#3a4560' }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* PDF / Transcript */}
        {session.transcriptPurchased ? (
          <div style={styles.pdfCard}>
            <div style={styles.pdfIcon}>📄</div>
            <div>
              <div style={styles.pdfTitle}>Session Report (PDF)</div>
              <div style={styles.pdfSub}>
                {pdfReady
                  ? 'Your full conversation transcript is ready to download.'
                  : 'Your report is being generated — this takes a few seconds…'}
              </div>
            </div>
            {pdfReady ? (
              <a href={downloadUrl} download style={styles.downloadBtn} target="_blank" rel="noreferrer">
                Download ↓
              </a>
            ) : (
              <div style={styles.generatingBadge}>
                <span style={styles.dot} /> Generating…
              </div>
            )}
          </div>
        ) : (
          <div style={styles.pdfCard}>
            <div style={styles.pdfIcon}>📄</div>
            <div style={{ flex: 1 }}>
              <div style={styles.pdfTitle}>Session Transcript PDF</div>
              <div style={styles.pdfSub}>Get a complete downloadable record of this session — $10</div>
            </div>
            <button
              onClick={handlePurchaseTranscript}
              disabled={transcriptLoading}
              style={{ ...styles.downloadBtn, opacity: transcriptLoading ? 0.6 : 1 }}
            >
              {transcriptLoading ? 'Redirecting…' : 'Purchase — $10'}
            </button>
          </div>
        )}

        {/* CTAs */}
        <div style={styles.ctaBlock}>
          {isDone ? (
            <>
              <p style={styles.ctaCopy}>
                Ready to unlock the next 4-lesson block? Your instructor picks up exactly where you left off.
              </p>
              <button onClick={() => navigate('/pricing')} style={styles.btnPrimary}>
                Continue Training →
              </button>
              <button onClick={() => navigate('/dashboard')} style={styles.btnSecondary}>
                Back to Dashboard
              </button>
            </>
          ) : (
            <>
              <p style={styles.ctaCopy}>
                Your session is saved. You can pick up right where you left off.
              </p>
              <button onClick={() => navigate('/v2-course')} style={styles.btnPrimary}>
                Resume Session →
              </button>
              <button onClick={() => navigate('/dashboard')} style={styles.btnSecondary}>
                Back to Dashboard
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#f0f4ff',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #1e2a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0a0e1a',
    position: 'sticky', top: 0, zIndex: 10,
  },
  logo: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: '#4f8ef7',
  },
  backBtn: {
    background: 'none', border: 'none',
    color: '#4a5568', fontSize: '13px', cursor: 'pointer', padding: '6px 12px',
  },
  signOutBtn: {
    background: 'none', border: 'none',
    color: '#4a5568', fontSize: '12px', cursor: 'pointer', padding: '6px 8px', letterSpacing: '0.04em',
  },
  content: {
    maxWidth: '600px',
    width: '100%',
    margin: '0 auto',
    padding: '48px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  titleBlock: {
    textAlign: 'center',
    marginBottom: '8px',
  },
  tagline: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    color: '#4f8ef7',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#f0f4ff',
    margin: '0 0 8px 0',
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '14px',
    color: '#4a5568',
    margin: 0,
  },
  summaryCard: {
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#4a5568',
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#f0f4ff',
  },
  levelChip: {
    padding: '3px 12px',
    backgroundColor: '#0f2040',
    border: '1px solid #4f8ef7',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#4f8ef7',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  pipsRow: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    paddingTop: '8px',
    borderTop: '1px solid #1e2a3a',
  },
  pipItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  },
  pip: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pipLabel: {
    fontSize: '10px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  pdfCard: {
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pdfIcon: {
    fontSize: '28px',
    flexShrink: 0,
  },
  pdfTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#f0f4ff',
    marginBottom: '4px',
  },
  pdfSub: {
    fontSize: '13px',
    color: '#4a5568',
    lineHeight: 1.5,
  },
  downloadBtn: {
    marginLeft: 'auto',
    flexShrink: 0,
    padding: '8px 20px',
    backgroundColor: '#4f8ef7',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  generatingBadge: {
    marginLeft: 'auto',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#4a5568',
  },
  dot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    backgroundColor: '#4f8ef7',
    borderRadius: '50%',
  },
  ctaBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '8px',
    textAlign: 'center',
  },
  ctaCopy: {
    fontSize: '15px',
    color: '#6b7a99',
    lineHeight: 1.7,
    margin: '0 0 8px 0',
    maxWidth: '440px',
  },
  btnPrimary: {
    padding: '14px 40px',
    backgroundColor: '#4f8ef7',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    maxWidth: '360px',
  },
  btnSecondary: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#4a5568',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '360px',
  },
  center: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '48px', gap: '16px',
  },
  spinner: {
    width: '36px', height: '36px',
    border: '3px solid #1e2a3a',
    borderTop: '3px solid #4f8ef7',
    borderRadius: '50%',
  },
  subtext: {
    color: '#4a5568', fontSize: '14px', margin: 0,
  },
};
